import { Box, Skeleton, Stack } from '@mui/material';
import { HairlineCard } from './HairlineCard';
import { palette } from '../theme/palette';

export type LoadingSkeletonVariant = 'cards' | 'form' | 'map' | 'ledger';

export interface LoadingSkeletonProps {
  /** The shape of the content that is on its way. */
  variant?: LoadingSkeletonVariant;
  /** How many cards or rows to sketch. */
  count?: number;
  /**
   * What a screen reader hears while it loads ("Loading"). The shapes are
   * decoration; this is the one thing announced.
   */
  label: string;
}

/** One card's worth of lines: a title, a line of detail, a shorter one. */
function CardLines() {
  return (
    <HairlineCard sx={{ p: 2 }}>
      <Skeleton variant="text" width="40%" sx={{ fontSize: 18 }} />
      <Skeleton variant="text" width="85%" />
      <Skeleton variant="text" width="60%" />
    </HairlineCard>
  );
}

/**
 * A page-shaped placeholder for content that takes a moment to arrive.
 *
 * Replaces the lone spinner a slow screen used to show under its header: two to
 * four seconds of an empty page with a 40px circle in it reads as broken, while
 * the outline of what is coming reads as loading — and the page does not jump
 * when it lands, because the outline already took its space.
 *
 * `role="status"` with `aria-busy`, so assistive technology hears one "Loading"
 * rather than a list of empty shapes.
 */
export function LoadingSkeleton({ variant = 'cards', count = 3, label }: LoadingSkeletonProps) {
  return (
    <Box role="status" aria-busy="true" aria-label={label} data-testid="loading-skeleton">
      {variant === 'map' && (
        <HairlineCard sx={{ p: 0, overflow: 'hidden' }}>
          <Skeleton
            variant="rectangular"
            animation="wave"
            sx={{
              height: { xs: 'calc(100vh - 260px)', md: 'calc(100vh - 220px)' },
              minHeight: 420,
            }}
          />
        </HairlineCard>
      )}

      {variant === 'form' && (
        <Stack spacing={2.5}>
          <Skeleton variant="rounded" height={40} />
          <HairlineCard sx={{ p: 3 }}>
            <Stack spacing={1.5} sx={{ alignItems: 'center' }}>
              <Skeleton variant="circular" width={56} height={56} />
              <Skeleton variant="text" width="40%" sx={{ fontSize: 18 }} />
              <Skeleton variant="rounded" width={180} height={40} />
            </Stack>
          </HairlineCard>
          <CardLines />
        </Stack>
      )}

      {variant === 'ledger' && (
        <Stack spacing={2}>
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: {
                xs: 'repeat(2, minmax(0, 1fr))',
                md: 'repeat(4, minmax(0, 1fr))',
              },
            }}
          >
            {[0, 1, 2, 3].map((i) => (
              <HairlineCard key={i} sx={{ p: 2 }}>
                <Skeleton variant="text" width="50%" />
                <Skeleton variant="text" width="70%" sx={{ fontSize: 24 }} />
              </HairlineCard>
            ))}
          </Box>
          <HairlineCard sx={{ p: 2 }}>
            {Array.from({ length: count }, (_, i) => (
              <Skeleton key={i} variant="text" sx={{ fontSize: 18, my: 0.5 }} />
            ))}
          </HairlineCard>
        </Stack>
      )}

      {variant === 'cards' && (
        <Stack spacing={2}>
          {Array.from({ length: count }, (_, i) => (
            <CardLines key={i} />
          ))}
        </Stack>
      )}
    </Box>
  );
}

/**
 * A navigation row that is waiting on the data that decides whether it is
 * offered — the same height as a real row, so nothing below it moves when the
 * answer arrives. For the dark rail; `aria-hidden` because it is not a
 * destination yet.
 */
export function NavRowSkeleton({ collapsed = false }: { collapsed?: boolean }) {
  return (
    <Box
      aria-hidden
      data-testid="nav-row-skeleton"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        px: collapsed ? 0 : 1.5,
        py: 1.1,
        justifyContent: collapsed ? 'center' : 'flex-start',
        // Same box as a rail row: the 19px icon sets the line, plus the row's
        // own 1.1 spacing top and bottom — so nothing below moves on arrival.
        '& .MuiSkeleton-root': { bgcolor: palette.background.surfaceOnDarkSoft },
      }}
    >
      <Skeleton variant="rounded" width={19} height={19} />
      {!collapsed && <Skeleton variant="rounded" width="60%" height={12} />}
    </Box>
  );
}
